import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Alert, Box, CircularProgress } from '@mui/material';
import { stripePromise } from '@/util/stripe';
import { useManualStripePaymentIntent } from '@/queries/payments/useManualStripePaymentIntent';
import StripePaymentRequestButton from './StripePaymentRequestButton';
import { ManualPaymentService } from '@/api/manualPaymentService';
import { getBuyPanelsPath } from '../route';
import applePayLogo from '@/res/logos/Apple-Pay-Mark/apple-pay-logo.png';
import googlePayLogo from '@/res/logos/Google-Pay-Mark/google-pay-logo.png';

interface ManualStripeWalletPaymentFormProps {
  requestUuid: string;
  token?: string;
  type: 'apple' | 'google';
  onSuccess?: () => void;
}

const WALLET_LOGOS = {
  apple: applePayLogo,
  google: googlePayLogo,
} as const;

export default function ManualStripeWalletPaymentForm({
  requestUuid,
  token,
  type,
  onSuccess,
}: ManualStripeWalletPaymentFormProps) {
  const { clientSecret, amount, error, loading } = useManualStripePaymentIntent(requestUuid, token, type);
  const routePrefix = getBuyPanelsPath(window.location.pathname, '');

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading || !clientSecret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const buildSuccessUrl = (paymentIntentId: string) => {
    const params = new URLSearchParams();
    params.set('requestId', requestUuid);
    params.set('payment_intent', paymentIntentId);
    if (token) {
      params.set('token', token);
    }

    return `${window.location.origin}${routePrefix}/manual-payment/success?${params.toString()}`;
  };

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: 'stripe' },
      }}
    >
      <div className="space-y-4 py-4">
        <StripePaymentRequestButton
          type={type}
          label="SolarCloud Balance Payment"
          amount={amount}
          onSuccess={() => {
            onSuccess?.();
          }}
          logo={WALLET_LOGOS[type]}
          clientSecret={clientSecret}
          orderUuid={requestUuid}
          beforePayment={async () => true}
          finalizePaymentIntent={async (paymentIntentId) => {
            const response = await ManualPaymentService.completeStripeIntent(requestUuid, paymentIntentId, token ?? undefined);
            if (response.confirmed) {
              window.location.assign(buildSuccessUrl(paymentIntentId));
              return true;
            }

            return false;
          }}
        />
      </div>
    </Elements>
  );
}
